import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { usePrefs } from 'wsm-common-data';
import logger from 'ws-scripts/modules/logger';
import Carousel from 'nuka-carousel';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';
import { setPage } from '../actions/setAppData';
import { addReviewData } from '../actions/setReviewData';
import trackGa from '../utils/trackEvent';
import Review from './Review';
import {
	defaultNumItemsPerSlide,
	defaultCurrentSlide,
	defaultNumSlides
} from '../utils/appConstants';

const controlPositionsMap = {
	'top-left': 'renderTopLeftControls',
	'top-center': 'renderTopCenterControls',
	'top-right': 'renderTopRightControls',
	'center-left': 'renderCenterLeftControls',
	'center-center': 'renderCenterCenterControls',
	'center-right': 'renderCenterRightControls',
	'bottom-left': 'renderBottomLeftControls',
	'bottom-center': 'renderBottomCenterControls',
	'bottom-right': 'renderBottomRightControls'
};

const SliderComponent = ({
	singleSource,
	reviewTruncationLength,
	labels,
	arrowStyle,
	fontSize,
	reviewDateFontSize,
	nextSlideControlPosition,
	previousSlideControlPosition,
	ratingStarFullFillColor
}) => {
	const { appData } = useSelector((state) => state);
	const reviewData = useSelector((state) => state.reviewData);
	const windowId = useSelector((state) => state.requestData.windowId);

	const dispatch = useDispatch();

	const {
		minRating,
		maxRating,
		excludeEmpty,
		sources,
		department,
		arrowColor,
		seeFullReviewColor,
		enableDemoData
	} = usePrefs();

	const sliderRef = useRef(null);

	const [view, setView] = useState('desktop');
	const [itemsPerSlide, setItemsPerSlide] = useState(defaultNumItemsPerSlide);
	const [currentSlide, setCurrentSlide] = useState(defaultCurrentSlide);
	const [numSlides, setNumSlides] = useState(defaultNumSlides);
	const [isLastSlide, setIsLastSlide] = useState(false);
	const [isFirstSlide, setIsFirstSlide] = useState(true);
	const [isEndReviews, setIsEndReviews] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isNextPageIndex, setIsNextPageIndex] = useState(0);

	useEffect(() => {
		const findView = (width) => {
			if (width < 650) {
				setView('mobile');
				setItemsPerSlide(1);
			}
			if (width >= 650 && width < 900) {
				setView('tablet');
				setItemsPerSlide(2);
			}
			setNumSlides(Math.ceil(reviewData.length / itemsPerSlide));
		};

		if (sliderRef.current) {
			findView(sliderRef.current.getBoundingClientRect().width);
			if (typeof ResizeObserver !== 'undefined') {
				const resizeObserver = new ResizeObserver((entries) => {
					findView(entries[0].contentRect.width);
				});
				resizeObserver.observe(sliderRef.current);
			}
		}
	}, [
		sliderRef,
		itemsPerSlide,
		reviewData.length,
		view,
		windowId,
		currentSlide
	]);

	const handleMoreReviews = async () => {
		setIsLoading(true);
		try {
			if (enableDemoData === 'true') {
				throw new Error('demo data enabled');
			}
			const response = await fetch(
				// eslint-disable-next-line max-len
				`/api/widget/ws-dealer-reviews/accountId/${
					appData.accountName
				}/minRating/${minRating}/maxRating/${maxRating}/excludeEmpty/${excludeEmpty}/sources/${sources}/department/${department}/page/${
					appData.page + 1
				}`,
				{
					timeout: 1200
				}
			);
			const data = await response.json();
			if (data && data.length > 0) {
				dispatch(setPage(appData.page + 1));
				dispatch(addReviewData(data));
				setIsLastSlide(false);
				setIsNextPageIndex(itemsPerSlide * currentSlide);
			} else {
				setIsEndReviews(true);
			}
		} catch (e) {
			setIsEndReviews(true);
			const msg = `Could not retrieve more reviews for ${appData.accountName} - ${e.message}`;
			logger.warn(msg);
		}
		setIsLoading(false);
		trackGa(windowId, `Carousel ${appData.page}`, 'Fetch more reviews');
	};

	const handleAfterSlide = (index) => {
		const localCurrentSlide = Math.ceil(index / itemsPerSlide + 1);
		setCurrentSlide(localCurrentSlide);

		if (index !== 0) {
			setIsFirstSlide(false);
		} else {
			setIsFirstSlide(true);
		}

		if (numSlides === localCurrentSlide) {
			setIsLastSlide(true);
		} else {
			setIsLastSlide(false);
		}
	};

	const getCarouselProps = (renderPreviousButton, renderNextButton) => {
		const previousRendererKey =
			controlPositionsMap[previousSlideControlPosition];
		const nextRendererKey = controlPositionsMap[nextSlideControlPosition];
		const carouselProps = {
			afterSlide: handleAfterSlide,
			slideIndex: isNextPageIndex,
			heightMode: 'max',
			wrapAround: false,
			speed: 700,
			scrollMode: 'remainder',
			transitionMode: 'scroll',
			easing: 'easePolyInOut',
			[previousRendererKey]: renderPreviousButton,
			[nextRendererKey]: renderNextButton,
			renderBottomCenterControls: null
		};

		if (
			previousRendererKey !== 'renderCenterLeftControls' &&
			nextRendererKey !== 'renderCenterLeftControls'
		) {
			carouselProps.renderCenterLeftControls = null;
		}
		if (
			previousRendererKey !== 'renderCenterRightControls' &&
			nextRendererKey !== 'renderCenterRightControls'
		) {
			carouselProps.renderCenterRightControls = null;
		}
		return carouselProps;
	};

	const arrowClick = (fn, direction, slideCount, localCurrentSlide) => {
		const additionalData = {
			totalItems: Math.ceil(slideCount / itemsPerSlide),
			currentItem: localCurrentSlide / itemsPerSlide + 1,
			label: direction
		};

		if (direction === 'Next') {
			setIsFirstSlide(false);
		}
		if (isLastSlide && direction === 'Next') {
			handleMoreReviews();
		}

		trackGa(
			windowId,
			`Carousel ${direction} Button`,
			'Move to next set of reviews',
			additionalData
		);
		fn();
	};

	const renderPreviousButton = ({
		previousSlide,
		slideCount,
		currentSlide: localCurrentSlide
	}) => (
		<button
			className="btn-unstyled"
			type="button"
			aria-label="previous"
			aria-disabled={isFirstSlide}
			disabled={isFirstSlide}
			onClick={() =>
				arrowClick(
					previousSlide,
					'Previous',
					slideCount,
					localCurrentSlide
				)
			}
		>
			<i
				className={`ddc-icon ddc-icon-${arrowStyle}-left opacity-${
					isFirstSlide ? '50' : '100'
				} rounded-circle bg-${arrowColor} p-3`}
				aria-hidden="true"
			/>
		</button>
	);

	const renderNextButton = ({
		nextSlide,
		slideCount,
		currentSlide: localCurrentSlide
	}) => (
		<button
			className="btn-unstyled"
			type="button"
			aria-label="next"
			aria-disabled={isLoading || (isLastSlide && isEndReviews)}
			disabled={isLoading || (isLastSlide && isEndReviews)}
			onClick={() =>
				arrowClick(nextSlide, 'Next', slideCount, localCurrentSlide)
			}
		>
			<i
				className={`ddc-icon ddc-icon-${arrowStyle}-right opacity-${
					isLoading || (isLastSlide && isEndReviews) ? '50' : '100'
				} rounded-circle bg-${arrowColor} p-3`}
				aria-hidden="true"
			/>
		</button>
	);

	const carouselProps = getCarouselProps(
		renderPreviousButton,
		renderNextButton
	);

	const ResponsiveSettings = {
		desktop: {
			...carouselProps,
			slidesToShow: itemsPerSlide,
			slidesToScroll: itemsPerSlide,
			...(reviewData.length <= itemsPerSlide && {
				renderCenterRightControls: null,
				renderCenterLeftControls: null,
				renderBottomCenterControls: null,
				dragging: false
			})
		},
		tablet: {
			...carouselProps,
			slidesToShow: itemsPerSlide,
			slidesToScroll: itemsPerSlide,
			...(reviewData.length <= itemsPerSlide && {
				renderCenterRightControls: null,
				renderCenterLeftControls: null,
				renderBottomCenterControls: null,
				dragging: false
			})
		},
		mobile: {
			...carouselProps,
			slidesToShow: itemsPerSlide,
			slidesToScroll: itemsPerSlide,
			...(reviewData.length <= itemsPerSlide && {
				renderCenterRightControls: null,
				renderCenterLeftControls: null,
				renderBottomCenterControls: null,
				dragging: false
			})
		}
	};

	if (reviewData.length > 0) {
		return (
			<div className="w-100 mt-4 mt-sm-10" ref={sliderRef}>
				<Carousel {...ResponsiveSettings[view]}>
					{reviewData.map((review) => (
						<Review
							key={review.id}
							review={review}
							singleSource={singleSource}
							windowId={windowId}
							reviewTruncationLength={reviewTruncationLength}
							labels={labels}
							fontSize={fontSize}
							reviewDateFontSize={reviewDateFontSize}
							seeFullReviewColor={seeFullReviewColor}
							ratingStarFullFillColor={ratingStarFullFillColor}
						/>
					))}
				</Carousel>
			</div>
		);
	} else {
		return (
			<Alert bsStyle="info" className="m-2 m-sm-10">
				{labels.get('NO_REVIEWS_ALERT_MESSAGE')}
			</Alert>
		);
	}
};

SliderComponent.propTypes = {
	reviewData: PropTypes.arrayOf(PropTypes.shape),
	singleSource: PropTypes.bool.isRequired,
	windowId: PropTypes.string.isRequired,
	reviewTruncationLength: PropTypes.number.isRequired,
	labels: PropTypes.shape({
		get: PropTypes.func
	}).isRequired,
	arrowStyle: PropTypes.string.isRequired,
	fontSize: PropTypes.string,
	reviewDateFontSize: PropTypes.string,
	nextSlideControlPosition: PropTypes.string,
	previousSlideControlPosition: PropTypes.string,
	ratingStarFullFillColor: PropTypes.string
};

SliderComponent.defaultProps = {
	reviewData: [],
	fontSize: '',
	reviewDateFontSize: ''
};

export default SliderComponent;
